import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Select,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useForm, Controller } from 'react-hook-form';
import { useBookingActions } from 'hooks/useBookingActions';
import { bookingStatus, invoiceStatus } from 'variables/general';
import { BsSearch } from 'react-icons/bs';
import { RxReset } from 'react-icons/rx';
import { RiFileExcel2Fill } from 'react-icons/ri';
import DatePickerInput from 'components/CustomDateInput/CustomDateInput';
import { totalRow } from 'state/roles-permissions';

import {
  listChannelAtom,
  listShowAtom,
  listUnitAtom,
  venueListAtom,
  zoneListAtom,
  paymentStatusListAtom,
  paymentMethodListAtom,
  listTrackingChargePaymentAtom,
  showTimeListAtom,
} from '../../state/show';
import SearchTable2 from 'components/Tables/SearchTable2';
import { columnsTrackingChargePaymentList } from 'variables/columnsData';
import dayjs from 'dayjs';

function DataTableTrackingChargePaymentReport() {
  const textColor = useColorModeValue('gray.700', 'white');
  const bookingActions = useBookingActions();
  const listShow = useRecoilValue(listShowAtom);
  const listTrackingChargePayment = useRecoilValue(listTrackingChargePaymentAtom);
  const totalRows = useRecoilValue(totalRow);
  const venueList = useRecoilValue(venueListAtom);
  const listChannel = useRecoilValue(listChannelAtom);
  const listUnit = useRecoilValue(listUnitAtom);
  const listZone = useRecoilValue(zoneListAtom);
  const listPaymentMethod = useRecoilValue(paymentMethodListAtom);
  const showTimeList = useRecoilValue(showTimeListAtom);
  const listPaymentStatus = useRecoilValue(paymentStatusListAtom);
  const [isFetching, setIsFetching] = useState(true);
  const [newTrackingChargePaymentList, setNewTrackingChargePaymentList] = useState([]);
  const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isExportExcel, setIsExportExcel] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);

  const toast = useToast();

  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    control: controlSearch,
    watch: watchSearch,
    reset: resetSearch,
    setValue: setValueSearch,
    formState,
  } = useForm();
  const { errors } = formState;
  const statusSearch = watchSearch('statusSearch');
  const channelSearch = watchSearch('channelSearch');
  const bookingCodeSearch = watchSearch('bookingCodeSearch');
  const bookerNameSearch = watchSearch('bookerNameSearch');
  const unitSearch = watchSearch('unitSearch');
  const paymentMethodSearch = watchSearch('paymentMethodSearch');
  const paymentStatusSearch = watchSearch('paymentStatusSearch');
  const showTimeSearch = watchSearch('showTimeSearch');
  const zoneSearch = watchSearch('zoneSearch');
  const venueSearch = watchSearch('venueSearch');
  const showSearch = watchSearch('showSearch');
  const invoiceResponseSearch = watchSearch('invoiceResponseSearch');
  const hasChargeSearch = watchSearch('hasChargeSearch');
  const allSearchValues = watchSearch();

  useEffect(() => {
    if (hasSearched) {
      setIsFilterDirty(true);
    }
  }, [JSON.stringify(allSearchValues)]);

  useEffect(() => {
    if (channelSearch) {
      bookingActions.getListUnits({ channel_id: channelSearch }, 0, '');
    }
  }, [channelSearch]);

  useEffect(async () => {
    setIsFetching(true);

    bookingActions.getListShow({ status: 1 }, 0, '');
    bookingActions.getListChannel({ status: 1 }, 0, '');
    bookingActions.getListVenue({ status: 1 }, 0, '');
    bookingActions.getListZones({ status: 1 }, 0, '');
    bookingActions.getListPaymentStatus({ status: 1 }, 0, '');
    bookingActions.getListPaymentMethod({ status: 1 }, 0, '');
    bookingActions.getListShowTime({ status: 1 }, 0, '');
    setValueSearch('from_showdate', new Date());
    setValueSearch('to_showdate', new Date());
    setValueSearch('paid_date_from', null);
    setValueSearch('paid_date_to', null);
    setIsFetching(false);
  }, []);

  useEffect(() => {
    if (listTrackingChargePayment) {
      const newList = listTrackingChargePayment.map((i) => ({
        ...i,
        typeSwitch: 'trackingChargePayment',
        filters: {
          show_id: showSearch,
          venue_id: venueSearch,
          booking_code: bookingCodeSearch,
          booker_name: bookerNameSearch,
          channel_id: channelSearch,
          unit_id: unitSearch,
          payment_method: paymentMethodSearch,
          payment_status: paymentStatusSearch,
          show_time: showTimeSearch,
          zone_id: zoneSearch,
          status: statusSearch ? statusSearch : 1,
        },
      }));
      setNewTrackingChargePaymentList(newList);
    }
  }, [listTrackingChargePayment]);

  const recallApi = async (fetchDataOptions) => {
    setIsFetching(true);
    // Only load data if user has searched before
    if (hasSearched) {
      const show_date_from = watchSearch('from_showdate')
        ? dayjs(watchSearch('from_showdate')).format('YYYY-MM-DD')
        : undefined;
      const show_date_to = watchSearch('to_showdate')
        ? dayjs(watchSearch('to_showdate')).format('YYYY-MM-DD')
        : undefined;
      const paid_date_from = watchSearch('paid_date_from')
        ? new Date(watchSearch('paid_date_from').setHours(0, 0, 0, 0))
        : undefined;
      const paid_date_to = watchSearch('paid_date_to')
        ? new Date(watchSearch('paid_date_to').setHours(23, 59, 59, 999))
        : undefined;
      await bookingActions.getListTrackingChargePayment(
        {
          show_id: showSearch,
          venue_id: venueSearch,
          booking_code: bookingCodeSearch,
          booker_name: bookerNameSearch,
          status: statusSearch,
          channel_id: channelSearch,
          unit_id: unitSearch,
          payment_method: paymentMethodSearch,
          payment_status: paymentStatusSearch,
          show_time: showTimeSearch,
          zone_id: zoneSearch,
          invoice_status: watchSearch('invoice_status'),
          invoice_response: invoiceResponseSearch,
          show_date_from: show_date_from,
          show_date_to: show_date_to,
          paid_date_from: paid_date_from,
          paid_date_to: paid_date_to,
          ...(hasChargeSearch !== '' && hasChargeSearch !== undefined
            ? { is_charge: hasChargeSearch === 'true' }
            : {}),
          sort_type: fetchDataOptions.sort_type,
          sort_field: fetchDataOptions.sort_field,
        },
        fetchDataOptions.pageIndex,
        fetchDataOptions.pageSize
      );
    }
    setIsFetching(false);
  };

  async function onSubmitSearch({
    bookingCodeSearch,
    bookerNameSearch,
    showSearch,
    venueSearch,
    statusSearch,
    channelSearch,
    unitSearch,
    paymentMethodSearch,
    paymentStatusSearch,
    showTimeSearch,
    zoneSearch,
    invoice_status,
    invoiceResponseSearch,
    hasChargeSearch,
  }) {
    const show_date_from = watchSearch('from_showdate')
      ? dayjs(watchSearch('from_showdate')).format('YYYY-MM-DD')
      : undefined;
    const show_date_to = watchSearch('to_showdate')
      ? dayjs(watchSearch('to_showdate')).format('YYYY-MM-DD')
      : undefined;
    const paid_date_from = watchSearch('paid_date_from')
      ? new Date(watchSearch('paid_date_from').setHours(0, 0, 0, 0))
      : undefined;
    const paid_date_to = watchSearch('paid_date_to')
      ? new Date(watchSearch('paid_date_to').setHours(23, 59, 59, 999))
      : undefined;

    // Validate: if no at least one complete date range is required
    const hasFullShowDate = !!(show_date_from && show_date_to);
    const hasFullPaidDate = !!(paid_date_from && paid_date_to);
    if (!hasFullShowDate && !hasFullPaidDate) {
      toast({
        description: 'Please select Date range (Show Date/Booked Date/Paid Date) before searching.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      if (isExportExcel) setIsExportExcel(false);
      return;
    }

    // Validate: show date range must be either both set or both empty
    if ((show_date_from && !show_date_to) || (!show_date_from && show_date_to)) {
      toast({
        description: 'Please enter both Show Date From and Show Date To',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      if (isExportExcel) setIsExportExcel(false);
      return;
    }

    // Validate: paid date range must be either both set or both empty
    if ((paid_date_from && !paid_date_to) || (!paid_date_from && paid_date_to)) {
      toast({
        description: 'Please enter both Paid Date From and Paid Date To',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      if (isExportExcel) setIsExportExcel(false);
      return;
    }

    if (isExportExcel) {
      setIsExportExcel(false);

      const params = {
        show_id: showSearch,
        venue_id: venueSearch,
        booking_code: bookingCodeSearch,
        booker_name: bookerNameSearch,
        status: statusSearch,
        channel_id: channelSearch,
        unit_id: unitSearch,
        payment_method: paymentMethodSearch,
        payment_status: paymentStatusSearch,
        show_time: showTimeSearch,
        zone_id: zoneSearch,
        invoice_status,
        invoice_response: invoiceResponseSearch,
        show_date_from,
        show_date_to,
        paid_date_from,
        paid_date_to,
        ...(hasChargeSearch !== '' && hasChargeSearch !== undefined
          ? { is_charge: hasChargeSearch === 'true' }
          : {}),
      };
      const response = await bookingActions.exportTrackingChargePayment(params);
      const outputFilename = `tracking_charge_payment_report_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', outputFilename);
      document.body.appendChild(link);
      link.click();
      return;
    }

    setIsFetching(true);
    setHasSearched(true);
    setIsFilterDirty(false);

    await bookingActions.getListTrackingChargePayment(
      {
        show_id: showSearch,
        venue_id: venueSearch,
        booking_code: bookingCodeSearch,
        booker_name: bookerNameSearch,
        status: statusSearch,
        channel_id: channelSearch,
        unit_id: unitSearch,
        payment_method: paymentMethodSearch,
        payment_status: paymentStatusSearch,
        show_time: showTimeSearch,
        zone_id: zoneSearch,
        invoice_status,
        invoice_response: invoiceResponseSearch,
        show_date_from,
        show_date_to,
        paid_date_from,
        paid_date_to,
        ...(hasChargeSearch !== '' && hasChargeSearch !== undefined
          ? { is_charge: hasChargeSearch === 'true' }
          : {}),
      },
      0,
      localStorage && localStorage.getItem('currentPageSizeStore')
        ? localStorage.getItem('currentPageSizeStore')
        : 10
    );
    setRefreshPagingDefault((prev) => !prev);
    setIsFetching(false);
  }

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Text
            float='left'
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            Tracking Charge Payment Report
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex ml='22px'></Flex>
        <Card>
          <form
            id='tracking-charge-payment-search-form'
            onSubmit={handleSubmitSearch(onSubmitSearch)}
          >
            <Flex
              direction='row'
              alignItems='center'
              mb={3}
              gap={4}
              flexWrap={'wrap'}
            >
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='sm' fontWeight='semibold'>
                  Show Date From
                </FormLabel>
                <Controller
                  name='from_showdate'
                  control={controlSearch}
                  render={({ field }) => (
                    <>
                      <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        mask='99/99/9999'
                        selected={watchSearch('from_showdate')}
                        selectsStart
                        startDate={watchSearch('from_showdate')}
                        endDate={watchSearch('to_showdate')}
                        minDate={new Date('01/01/2020')}
                      />
                      {watchSearch('to_showdate') !== null && watchSearch('from_showdate') === null && (
                          <Text color='red' fontSize='xs'>
                            Show Date From is required
                          </Text>
                        )}
                    </>
                  )}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='sm' fontWeight='semibold'>
                  Show Date To
                </FormLabel>
                <Controller
                  name='to_showdate'
                  control={controlSearch}
                  render={({ field }) => (
                    <>
                    <DatePickerInput
                      onChange={(value) => field.onChange(value)}
                      selected={field.value}
                      mask='99/99/9999'
                      selectsEnd
                      startDate={watchSearch('from_showdate')}
                      endDate={watchSearch('to_showdate')}
                      minDate={watchSearch('from_showdate') || new Date("01/01/2020")}
                    />
                    {watchSearch('from_showdate') !== null && watchSearch('to_showdate') === null && (
                          <Text color='red' fontSize='xs'>
                            Show Date From is required
                          </Text>
                        )}
                    </>

                  )}
                />
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Paid Date From
                </FormLabel>
                <Controller
                  name="paid_date_from"
                  control={controlSearch}
                  render={({ field }) => (
                    <>
                      <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        mask="99/99/9999"
                        selected={watchSearch("paid_date_from")}
                        selectsStart
                        startDate={watchSearch("paid_date_from")}
                        endDate={watchSearch("paid_date_to")}
                        minDate={new Date("01/01/2020")}
                      />
                      {watchSearch("paid_date_to") !== null && watchSearch('paid_date_from') === null && (
                          <Text color='red' fontSize='xs'>
                            Paid Date To is required
                          </Text>
                        )}
                    </>
                  )}
                />
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                 Paid Date To
                </FormLabel>
                <Controller
                  name="paid_date_to"
                  control={controlSearch}
                  render={({ field }) => (
                    <>
                      <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch("paid_date_to")}
                        mask="99/99/9999"
                        selectsEnd
                        startDate={watchSearch("paid_date_from")}
                        endDate={watchSearch("paid_date_to")}
                        minDate={watchSearch("paid_date_from") || new Date("01/01/2020")}
                      />
                      {watchSearch("paid_date_from") !== null && watchSearch('paid_date_to') === null && (
                          <Text color='red' fontSize='xs'>
                            Paid Date From is required
                          </Text>
                        )}
                    </>
                  )}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  E-Invoice
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='E-Invoice'
                  width='200px'
                  size='sm'
                  fontSize='sm'
                  name='invoiceResponseSearch'
                  {...registerSearch('invoiceResponseSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  Booking Code
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='Booking Code'
                  width='200px'
                  size='sm'
                  fontSize='sm'
                  name='bookingCodeSearch'
                  {...registerSearch('bookingCodeSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  Booking information,...
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='Booking information,...'
                  minW='80px'
                  maxW='185px'
                  size='sm'
                  fontSize='sm'
                  name='bookerNameSearch'
                  {...registerSearch('bookerNameSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Show
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue='0'
                  size='sm'
                  id='showSearch'
                  name='showSearch'
                  {...registerSearch('showSearch')}
                  width='100px'
                >
                  <option value=''>All</option>
                  {listShow.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Venue
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  size='sm'
                  id={'venueSearch'}
                  name='venueSearch'
                  {...registerSearch('venueSearch')}
                >
                  <option value=''>All</option>
                  {venueList.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Zone
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  size='sm'
                  name='zoneSearch'
                  {...registerSearch('zoneSearch')}
                  width='200px'
                >
                  <option value=''>All</option>
                  {listZone.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Channel
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  size='sm'
                  name='channelSearch'
                  {...registerSearch('channelSearch')}
                  width='200px'
                >
                  <option value=''>All</option>
                  {listChannel.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              {channelSearch && (
                <Box direction='row' alignItems='center' display='block'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Unit
                  </FormLabel>
                  <Select
                    ms='4px'
                    type='text'
                    size='sm'
                    id='unit_search'
                    name='unitSearch'
                    {...registerSearch('unitSearch')}
                  >
                    <option value=''>All</option>
                    {listUnit.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Show Time
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue=''
                  size='sm'
                  name='showTimeSearch'
                  {...registerSearch('showTimeSearch')}
                >
                  <option value=''>All</option>
                  {showTimeList.map((item, index) => (
                    <option key={index} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Booking Status
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue='0'
                  size='sm'
                  id='statusSearch'
                  name='statusSearch'
                  {...registerSearch('statusSearch')}
                  width='100px'
                >
                  {bookingStatus.map((item, index) => (
                    <option key={index} value={item.Id}>
                      {item.Name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Invoice Status
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue='-1'
                  size='sm'
                  name='invoice_status'
                  {...registerSearch('invoice_status')}
                  width='100px'
                >
                  {invoiceStatus.map((item, index) => (
                    <option key={index} value={item.Id}>
                      {item.Name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Payment Method
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  placeholder=''
                  size='sm'
                  name='paymentMethodSearch'
                  {...registerSearch('paymentMethodSearch')}
                >
                  <option value=''>All</option>
                  {listPaymentMethod.map((item, index) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel
                  htmlFor='role-search-lbl'
                  fontWeight='semibold'
                  fontSize='xs'
                  ms='4px'
                >
                  Payment Status
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue=''
                  size='sm'
                  name='paymentStatusSearch'
                  {...registerSearch('paymentStatusSearch')}
                  width='100px'
                >
                  <option value=''>All</option>
                  {listPaymentStatus.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Has Charge?
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue=''
                  size='sm'
                  name='hasChargeSearch'
                  {...registerSearch('hasChargeSearch')}
                  width='100px'
                >
                  <option value=''>All</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Select>
              </Box>
            </Flex>
            <Text color='red' fontSize={14}>
              {Object.values(errors)[0]?.message}
            </Text>
            <Flex justify='center' align='center' gap={{ base: "4px", md: "8px" }} mt={3} flexWrap="wrap">
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<RxReset />}
                  variant='primary'
                  w={{ base: "70px", md: "88px" }}
                  h='33px'
                  fontSize={{ base: "sm", md: "md" }}
                  onClick={() => {
                    resetSearch();
                    setValueSearch('from_showdate', new Date());
                    setValueSearch('to_showdate', new Date());
                    setValueSearch('paid_date_from', null);
                    setValueSearch('paid_date_to', null);
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<RiFileExcel2Fill />}
                  variant='primary'
                  w={{ base: "80px", md: "100px" }}
                  h='33px'
                  fontSize={{ base: "sm", md: "md" }}
                  isDisabled={newTrackingChargePaymentList.length === 0 || isFilterDirty}
                  onClick={() => {
                    setIsExportExcel(true);
                  }}
                  type='submit'
                >
                  <Text display={{ base: "none", md: "block" }}>Export Excel</Text>
                  <Text display={{ base: "block", md: "none" }}>Export</Text>
                </Button>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<BsSearch />}
                  variant='primary'
                  w={{ base: "70px", md: "88px" }}
                  h='33px'
                  fontSize={{ base: "sm", md: "md" }}
                  type='submit'
                >
                  Search
                </Button>
              </Box>
            </Flex>
          </form>
        </Card>
        <div className="header-blue-600">
          <SearchTable2
            tableData={newTrackingChargePaymentList}
            columnsData={columnsTrackingChargePaymentList}
            totalRow={totalRows.trackingChargePayment}
            listPermissions={[69]}
            actionsActive={[]}
            loadData={(fetchDataOptions) => recallApi(fetchDataOptions)}
            isFetching={isFetching}
            refreshPagingDefault={refreshPagingDefault}
            sizeAllColumns={10}
          />
        </div>
      </CardBody>
    </Card>
  );
}

export default DataTableTrackingChargePaymentReport;
